/**
 * HORO-10504: opens the right Zara modal automatically when a visitor
 * arrives from the Zara-launch marketing email — `?utm_campaign=
 * zara_launch` on the link (QUERY_PARAM_UTM_CAMPAIGN / ZARA_LAUNCH_CAMPAIGN_VALUE
 * in const.js). Keyed on the campaign value specifically, not just
 * `utm_source=email`, so only this campaign's links trigger it.
 *
 * Tier-aware: reuses the exact same gate a real Zara tap already goes
 * through (MarketplaceActionButton / common/Menu/MenuContent's
 * handleAdvisorTap) rather than always opening the subscribe pitch —
 * a premium subscriber clicking the same broadly-sent email link
 * shouldn't see "Subscribe to Chat" for something they already have.
 *
 * Shape mirrors useAuth.js/useABBannerTesting.js: read the query param,
 * dispatch a redux action, then strip the param (so a refresh or
 * back-navigation doesn't keep re-triggering it).
 */
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { QUERY_PARAM_UTM_CAMPAIGN, ZARA_ENABLED, ZARA_LAUNCH_CAMPAIGN_VALUE } from '../const';
import {
    ADVISOR_LOCK_REASON,
    ZARA_ADVISOR_ALIAS,
    getAdvisorLockReason,
    isPremiumSubscriber,
    shouldShowZaraWelcome
} from '../service/advisorAccess';
import { getSeenAdvisorIntros, getUser } from '../store/slices/authSlice';
import { setShowZaraUpsellModal, setShowZaraWelcomeModal } from '../store/slices/modalsSlice';
import { useZaraTrial } from './useZaraTrial';

const useZaraEmailEntry = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const user = useSelector(getUser);
    const seenAdvisorIntros = useSelector(getSeenAdvisorIntros);
    const { isExhausted: zaraTrialExhausted } = useZaraTrial();
    const isFromZaraCampaign = searchParams.get(QUERY_PARAM_UTM_CAMPAIGN) === ZARA_LAUNCH_CAMPAIGN_VALUE;

    const clearSearchParams = useCallback(() => {
        searchParams.delete(QUERY_PARAM_UTM_CAMPAIGN);
        setSearchParams(searchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!isFromZaraCampaign) return;

        // HORO-10504: kill switch — a marketing link can go out (or keep
        // circulating) independent of frontend eng's own launch readiness,
        // so this hook otherwise has zero awareness of whether Zara is
        // actually live. The param is still stripped below either way,
        // matching this hook's existing "never re-trigger on refresh"
        // contract regardless of which branch ran.
        if (ZARA_ENABLED) {
            const isPremiumUser = isPremiumSubscriber(user);
            const lockReason = getAdvisorLockReason(ZARA_ADVISOR_ALIAS, { isUser: !!user?.id, isPremiumUser, zaraTrialExhausted });
            // HORO-10599: REGISTER (anon) now also opens the upsell modal —
            // ZaraUpsellModal is variant-aware and renders the register
            // pitch for an anon viewer, the subscribe pitch for a
            // trial-exhausted registered one.
            if (lockReason === ADVISOR_LOCK_REASON.REGISTER || lockReason === ADVISOR_LOCK_REASON.SUBSCRIBE) {
                dispatch(setShowZaraUpsellModal(true));
            } else if (shouldShowZaraWelcome(ZARA_ADVISOR_ALIAS, { isPremiumUser, seenAdvisorIntros })) {
                dispatch(setShowZaraWelcomeModal(true));
            }
            // Premium + already seen her welcome: land normally, no modal —
            // this campaign has nothing new to tell them.
        }
        clearSearchParams();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFromZaraCampaign]);
};

export default useZaraEmailEntry;
